// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material";
import { Router } from "@angular/router";

// Third party packages
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { distinctUntilChanged, finalize, startWith } from "rxjs/operators";

// Custom packages - Models
import { AuthService } from "src/app/modules/auth/auth.service";
import {
  activeServicesRequiring, bindServiceLinks, DEFAULT_ADDITIONAL_SERVICE_FLAGS, ServiceLinkRefusal
} from "src/app/modules/companies/additional-services";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { PricesService } from "src/app/modules/prices/prices.service";
import { SubscriptionsService } from "src/app/modules/subscriptions/subscriptions.service";
import { Company } from "src/app/shared/models/company/company.model";
import { IPrice } from "src/app/shared/models/price/price.interface";
import { IArchediSubscription } from "src/app/shared/models/subscription/subscription.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-add-company",
  templateUrl: "./add-company.page.html",
  styleUrls: ["./add-company.page.scss"],
})
export class AddCompanyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add property form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  formSC: FormGroup = new FormGroup({});
  model: any = {};
  modelSC: any = {};
  modelSCInit: any = {};
  anagraphicFields: FormlyFieldConfig[] = [];
  contractFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  optionsSC: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  formBtnOptionsSC: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Add property form

  archediSubscriptions: IArchediSubscription[] = [];
  defaultFee = 0;
  defaultAvailableSpace = 0;
  defaultExtraSpaceFee = 0;
  defaultStructurePrice = 0;
  defaultNfcAccessFee = 0;
  defaultSuppliersFee = 0;

  prices: IPrice[] = [];
  availableSubscriptions: any[];
  availableSubscriptionsKeys: any[];

  defaultStartDate: string;
  defaultEndDate: string;

  detailCardIsLoading = true;
  contractCardIsLoading = true;


  // List of codes
  firstScanCodes = this.configService.availableScanCodes;
  firstFixedPackCodes = this.configService.availableFixedPackageCodes;
  scanInsNextCodesForfait = this.configService.availableInsCodesNextForfait;
  scanNextCodesForfait = this.configService.availableScanCodesNextForfait;

  /**
   * Class constructor
   */
  constructor(
    private authService: AuthService,
    public helperService: HelperService,
    public configService: ConfigService,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private router: Router,
    private toastrService: ToastrService,
    private subscriptionsService: SubscriptionsService,
    private pricesService: PricesService,
    private dialog: MatDialog
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("companies.add.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Se default start and end date
    this.defaultStartDate = moment().toISOString();
    this.defaultEndDate = moment().add(1, "years").subtract(1, 'days').toISOString();

    this.getPrices();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Get prices from backend
   *
   * @since 1.0.0
   */
  getPrices(): void {
    this.subscriptions.push(
      this.pricesService.getList(0, 15).subscribe((res) => {
        if (res.status) {
          this.prices.push(...res.data.items);
          this.getSubscriptionsAndInitForm();
        }
      })
    );
  }

  /**
   * Load all subscription data frmo BE
   *
   * @since 1.0.0
   */
  getSubscriptionsAndInitForm(): void {
    this.subscriptionsService.getList().subscribe(
      (res) => {
        if (res.status) {
          this.archediSubscriptions = res.data.items;

          // Init default value from FULL subscription
          const index = this.archediSubscriptions.findIndex((el: any) => {
            return el.key === "FULL";
          });
          if (index > -1) {
            this.defaultFee = this.archediSubscriptions[index].fee;
            this.defaultAvailableSpace = this.archediSubscriptions[
              index
            ].availableSpace;
            this.defaultExtraSpaceFee = this.archediSubscriptions[
              index
            ].extraSpaceFee;
            this.defaultStructurePrice = this.archediSubscriptions[
              index
            ].structurePrice;
            this.defaultNfcAccessFee = this.archediSubscriptions[
              index
            ].nfcAccessFee;
            this.defaultSuppliersFee = this.archediSubscriptions[
              index
            ].suppliersFee;
          }

          const subscriptions = res.data.items.map((item: any) => {
            return {
              value: item,
              label: item.name,
            };
          });
          this.availableSubscriptions = subscriptions.filter((element: any) => {
            return (
              element.value.key !== "QRANTINCENDIO"
            );
          });
          this.availableSubscriptionsKeys = this.availableSubscriptions.map(
            (element: any) => {
              return {
                value: element.value.key,
                label: element.value.name,
              };
            }
          );

          // Create an empty value
          const noSubscription = {
            value: "noSubscription",
            label: this.translateService.instant(
              "subscriptions.fields.noSubscription"
            ),
          };
          this.availableSubscriptionsKeys.push(noSubscription);

          // Init add form
          this.initForm();

          this.detailCardIsLoading = false;
          this.contractCardIsLoading = false;
        }
      },
      (err) => {
        this.helperService.handleError(err);
      }
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = new Company();
    this.modelSC = new Company();

    const pricesInit = this.prices.map((el: any) => {
      return {
            key: el.key,
            descr: el.description,
            price: el.fee,
            discount: 0,
          }; 
    })
    this.modelSCInit = new Company();
    this.modelSCInit.subscription = {
      // Section 1.
      documentUpload: true,
      invoiceHolder: "property",
      key: "FULL",
      // code: "ABB010",
      fee: this.defaultFee,
      availableSpace: this.defaultAvailableSpace,
      // in MB
      totalStorageSpaceUsed: 0,
      // Section 2.
      structure: true,
      structurePrice: this.defaultStructurePrice,
      // Section 3.
      firstPriceList: true,
      // Section 4.
      scanInsNext: true,
      flatRate: false,
      // Section 5.
      scanNext: true,
      flatRateScan: false,
      prices: pricesInit,
      extraSpaceFee: this.defaultExtraSpaceFee,
      nfcAccess: false,
      // nfcAccessFee: this.defaultNfcAccessFee,
      // Additional services: on by default with the document upload (on above),
      // except the "Nota informativa"; each one has its own fee
      safetyVideoResidents: true,
      safetyVideoResidentsFee: 0,
      safetyVideoSuppliers: true,
      safetyVideoSuppliersFee: 0,
      fireSafety: true,
      fireSafetyFee: 0,
      informativeNote: false,
      informativeNoteFee: 0,
      suppliers: false,
      // suppliersFee: this.defaultSuppliersFee,
      // Privacy services: off by default
      privacy: false,
      privacySag: false,
      privacyFee: 0,
    }

    // For price list sorting
    let index = 0;

    const categoriesOptions: any[] = [];
    const rawCategories = this.translateService.instant("companies.categories");
    for (const trans of Object.keys(rawCategories)) {
      categoriesOptions.push({
        label: rawCategories[trans],
        value: trans,
      });
    }

    // Set fields
    this.anagraphicFields = [
      // ...this.anagraphicFields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "name",
            templateOptions: {
              label: this.translateService.instant("companies.fields.name"),
              placeholder: this.translateService.instant(
                "companies.fields.name"
              ),
              required: true,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "select",
            key: "types",
            templateOptions: {
              label: this.translateService.instant("companies.fields.types"),
              placeholder: this.translateService.instant(
                "companies.fields.types"
              ),
              required: true,
              multiple: true,
              options: [
                {
                  value: "owner",
                  label: this.translateService.instant("companies.types.owner"),
                },
                {
                  value: "manager",
                  label: this.translateService.instant(
                    "companies.types.manager"
                  ),
                },
              ],
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "vatNumber",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.vatNumber"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.vatNumber"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "taxCode",
            templateOptions: {
              label: this.translateService.instant("companies.fields.taxCode"),
              placeholder: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "email",
            templateOptions: {
              label: this.translateService.instant("companies.fields.email"),
              placeholder: this.translateService.instant(
                "companies.fields.email"
              ),
              required: true,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "pec",
            templateOptions: {
              label: this.translateService.instant("companies.fields.pec"),
              placeholder: this.translateService.instant(
                "companies.fields.pec"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "sdiCode",
            templateOptions: {
              label: this.translateService.instant("companies.fields.sdiCode"),
              placeholder: this.translateService.instant(
                "companies.fields.sdiCode"
              ),
              required: false,
              minLength: 6,
              maxLength: 7,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "legalAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalAddress"
              ),
              required: true,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "operativeAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.operativeAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.operativeAddress"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "phone",
            templateOptions: {
              label: this.translateService.instant("companies.fields.phone"),
              placeholder: this.translateService.instant(
                "companies.fields.phone"
              ),
              required: true,
              minLength: 1,
              maxLength: 15,
              pattern: this.configService.phonePattern,
            },
          },
          {
            className: "col-12 col-md-8",
            type: "input",
            key: "webSiteUrl",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.webSiteUrl"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.webSiteUrl"
              ),
              required: false,
              minLength: 1,
              maxLength: 100,
              pattern: this.configService.webSitePattern,
            },
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.legalRepresentative"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "legalName",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalName"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalName"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "legalLastName",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalLastName"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalLastName"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "legalTaxCode",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.taxCode"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "address",
            key: "legalResidentialAddress",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalResidentialAddress"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalResidentialAddress"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "legalEmail",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.legalEmail"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.legalEmail"
              ),
              required: false,
              minLength: 5,
              maxLength: 320,
              type: "email",
            },
          },
        ],
      },
    ];

    this.contractFields = [
      // ...this.contractFields,
      // 1. PLATFORM SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        hideExpression: (): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        hooks: {
          onInit: (): void => {
            this.updateSubscriptionCodes();
          },
        },
        fieldGroup: [
          {
            className: "col-12 boxForm",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subscription"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.documentUpload",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.documentUpload.title"
              ),
            },
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.noticeTacitRenewalContract",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.noticeTacitRenewalContract.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.noticeTacitRenewalContract.title"
              ),
            },
          },
          {
            className: "col-0 col-md-4",
          }, 
          {
            className: "col-12 col-md-4",
            type: "select",
            key: "subscription.invoiceHolder",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.invoiceHolder.title"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: "property",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.property"
                  ),
                },
                {
                  value: "admin",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.admin"
                  ),
                },
                {
                  value: "owner",
                  label: this.translateService.instant(
                    "companies.fields.subscription.invoiceHolder.owner"
                  ),
                },
              ],
            },
            defaultValue:
              this.modelSC.subscription && this.modelSC.subscription.invoiceHolder
                ? this.modelSC.subscription.invoiceHolder
                : "property",
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload",
              "templateOptions.disabled":
                "!model.subscription.documentUpload",
            },
          },
          {
            className: "col-0 col-md-8",
          },
          {
            className: "col-4",
            type: "select",
            key: "subscription.key",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.subscriptionType"
              ),
              multiple: false,
              options: this.availableSubscriptionsKeys,
              required: true,
            },
            defaultValue:
              this.modelSC.subscription && this.modelSC.subscription.key
                ? this.modelSC.subscription.key
                : "FULL",
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload",
              "templateOptions.disabled":
                "!model.subscription.documentUpload",
            },
          },
          {
            className: "col-4",
            type: "input",
            key: "subscription.fee",
            defaultValue: this.defaultFee,
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY'",
            },
          },
          {
            className: "col-0 col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.availableSpace",
            defaultValue: this.defaultAvailableSpace,
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.availableSpace"
              ),
              type: "number",
              min: 0,
              max: 1000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.documentUpload && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY'",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.totalStorageSpaceUsed",
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.totalStorageSpaceUsed"
              ),
              type: "number",
              step: 0.001,
              min: 0,
              max: 50000,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-0 col-md-4",
          },
          {
            className: "col-0 col-md-10",
          },
        ],
      },
      // 2. STRUCTURE SUBSCRIPTION
      {
        fieldGroupClassName: "row",
        hideExpression: (model): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.structure.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.structure",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structure.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY' && model.subscription.key !== 'FREE' && model.subscription.key !== 'FREEBASIC'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.key === 'FREE' || model.subscription.key === 'FREEBASIC'))",
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.structurePrice",
            defaultValue: this.defaultStructurePrice,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.structurePrice"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.structure",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.key === 'FREE' || model.subscription.key === 'FREEBASIC' || !model.subscription.structure))",
            },
          },
        ],
      },
      // 3. FIRST BALANCE
      {
        fieldGroupClassName: "row",
        hideExpression: (model): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.firstBalance.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.firstPriceList",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.firstPriceList.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY'))",
            },
          },
          {
            className: "col-1",
          },
        ],
      },
      // 4. SUBSEQUENT INSERTIONS
      {
        fieldGroupClassName: "row",
        hideExpression: (model): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentInsertions.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.scanInsNext",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanInsNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.flatRate))",
            },
          },
          {
            className: "col-8",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.flatRate",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.scanInsNext))",
            },
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalDoc",
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalDoc"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalPdf",
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalPdf"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
          },
        ],
      },
      // 5. SUBSEQUENT SCANS
      {
        fieldGroupClassName: "row",
        hideExpression: (model): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.subsequentScans.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.scanNext",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.scanNext.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.flatRateScan))",
            },
          },
          {
            className: "col-8",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.flatRateScan",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRateScan.title"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "!model.subscription.documentUpload || (model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY' || model.subscription.scanNext))",
            },
          },
          {
            className: "col-8",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA4",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA4"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA3",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA3"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA2",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA2"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-1",
          },
          {
            className: "col-2",
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA1",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA1"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalA0",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalA0"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: "subscription.totalOutFormat",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.flatRate.totalOutFormat"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              disabled: true,
            },
            defaultValue: 0,
          },
          {
            className: "col-1",
          },
        ],
      },
      // 6. SUBSCRIPTION OPTIONS
      {
        fieldGroupClassName: "row",
        hideExpression: (model): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsServices.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          // Each additional service has its own flag and fee (not tied to documentUpload)
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsServices.safetyVideo"
              ) +
              "</strong></div>",
          },
          ...this.additionalServiceFields(
            "safetyVideoResidents",
            "companies.fields.subscription.optionsServices.safetyVideoResidents"
          ),
          ...this.additionalServiceFields(
            "safetyVideoSuppliers",
            "companies.fields.subscription.optionsServices.safetyVideoSuppliers"
          ),
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsServices.fireSafety"
              ) +
              "</strong></div>",
          },
          ...this.additionalServiceFields(
            "fireSafety",
            "companies.fields.subscription.optionsServices.fireSafety",
            "col-md-4"
          ),
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.informativeNote"
              ) +
              "</strong></div>",
          },
          ...this.additionalServiceFields(
            "informativeNote",
            "companies.fields.subscription.informativeNote"
          ),
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsNfc.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.nfcAccess",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccess"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.nfcAccessFee",
            // defaultValue: this.defaultNfcAccessFee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
            },
          },
          {
            className: "col-12 col-md-2",
            type: "input",
            key: "subscription.nfcAccessTagFee",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagFee"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.nfcAccessTagFee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.nfcAccess",
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.nfcAccess",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateNfcAccess",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateNfcAccess"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.nfcAccess",
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsSupplierEvaluation.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.suppliers",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliers"
              ),
              required: false,
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.suppliersFee",
            // defaultValue: this.defaultSuppliersFee,
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.suppliers",
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDateSuppliers"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.suppliers",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDateSuppliers",
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDateSuppliers"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.suppliers",
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "checkbox",
            key: "subscription.suppliersNotify",
            defaultValue: true,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.suppliersNotify"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.suppliersNotify"
              ),
              required: false,
            },
          },
          {
            className: "col-md-8",
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.optionsPrivacy.sectionTitle"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacy",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.privacy"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  this.modelSC.subscription.privacySag
                );
              },
            },
          },
          {
            className: "col-12 col-md-2",
            type: "checkbox",
            key: "subscription.privacySag",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.privacySag"
              ),
              required: false,
            },
            expressionProperties: {
              "templateOptions.disabled": (): boolean => {
                return (
                  this.modelSC.subscription.privacy
                );
              },
            },
          },
          {
            className: "col-12 col-md-4",
            type: "input",
            key: "subscription.privacyFee",
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant("subscriptions.fields.fee"),
              placeholder: this.translateService.instant(
                "subscriptions.fields.fee"
              ),
              type: "number",
              step: 0.1,
              min: 0,
              max: 10000,
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
            },
          },
          {
            className: "col-md-4",
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.startDatePrivacy",
            defaultValue: moment().toDate(),
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.startDatePrivacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.startDatePrivacy"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
            },
          },
          {
            className: "col-12 col-md-4",
            type: "datepicker",
            key: "subscription.endDatePrivacy",
            defaultValue: moment().add(1, 'years').subtract(1, 'days').toDate(),
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.endDatePrivacy"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.endDatePrivacy"
              ),
            },
            expressionProperties: {
              "templateOptions.required": "model.subscription.privacy || model.subscription.privacySag",
            },
          },
        ],
      },
      // 7. PRICE LIST
      {
        fieldGroupClassName: "row",
        hideExpression: (model): boolean =>
          this.model.types ? !this.model.types.includes("manager") : true,
        fieldGroup: [
          {
            className: "col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "companies.fields.subscription.priceList.title"
              ) +
              "</strong></div><br />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                `prices.types.${this.prices[index].name}`
              ) +
              "</div>",
          },
          {
            key: `subscription.prices[${index}].key`,
            defaultValue: this.prices[index].key,
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index}].price`,
            defaultValue: this.prices[index].fee,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.price"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.priceService1"
              ),
              required: false,
              type: "number",
              step: 0.1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-12 col-md-3",
            type: "input",
            key: `subscription.prices[${index++}].discount`,
            defaultValue: 0,
            templateOptions: {
              label: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              placeholder: this.translateService.instant(
                "companies.fields.subscription.priceList.discount"
              ),
              required: false,
              type: "number",
              step: 1,
              min: 0,
              max: 100,
            },
          },
          {
            className: "col-0 col-md-6",
          },
          {
            className: "col-12",
            template: "<hr />",
          },
          {
            className: "col-12 section-label sub-section-label",
            template:
              "<div>" +
              this.translateService.instant(
                "companies.fields.subscription.additionalStorage.sectionTitle"
              ) +
              "</div>",
          },
          {
            className: "col-3",
            type: "input",
            key: "subscription.extraSpaceFee",
            defaultValue: this.defaultExtraSpaceFee,
            templateOptions: {
              label: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              placeholder: this.translateService.instant(
                "subscriptions.fields.extraSpaceFee"
              ),
              type: "number",
              min: 0,
              max: 500,
            },
            expressionProperties: {
              "templateOptions.required":
                "model.subscription.key && model.subscription.key !== 'noSubscription' && model.subscription.key !== 'PRIVACY'",
              "templateOptions.disabled":
                "model.subscription.key && (model.subscription.key === 'noSubscription' || model.subscription.key === 'PRIVACY')",
            },
          },
        ],
      },
      // // Notes
      // {
      //   fieldGroupClassName: "row",
      //   fieldGroup: [
      //     {
      //       className: "col-12",
      //       template:
      //         "<hr /><div><strong>" +
      //         this.translateService.instant(
      //           "companies.fields.subscription.notes"
      //         ) +
      //         "</strong></div>",
      //     },
      //     {
      //       className: "col-12",
      //       type: "textarea",
      //       key: "subscription.notes",
      //       templateOptions: {
      //         rows: 5,
      //       },
      //     },
      //   ],
      // },
    ];
  }

  /**
   * Build the "flag + fee" row of a single additional service.
   * The flag starts on for the services pre-selected with the document upload
   * (on by default), off for the others.
   * The fee is not shown: its control stays in the form (hidden, not required,
   * default 0) so the saved contract keeps a fee for every service.
   *
   * @since 3.0.0
   *
   * @param flag Subscription flag key (its fee is stored in `${flag}Fee`)
   * @param labelKey Translation key of the checkbox label
   * @param widthClass Bootstrap width of the checkbox from md up: `col-md-auto` keeps
   *   it as wide as its label, a fixed column lets a long label wrap
   * @returns Formly fields of the row (checkbox, hidden fee): consecutive services share a line
   */
  additionalServiceFields(
    flag: string,
    labelKey: string,
    widthClass: string = "col-md-auto"
  ): FormlyFieldConfig[] {
    return [
      {
        className: `col-12 ${widthClass}`,
        type: "checkbox",
        key: `subscription.${flag}`,
        defaultValue: DEFAULT_ADDITIONAL_SERVICE_FLAGS.indexOf(flag) > -1,
        templateOptions: {
          label: this.translateService.instant(labelKey),
          required: false,
        },
      },
      {
        className: "d-none",
        type: "input",
        key: `subscription.${flag}Fee`,
        defaultValue: 0,
        templateOptions: {
          label: this.translateService.instant("subscriptions.fields.fee"),
          placeholder: this.translateService.instant("subscriptions.fields.fee"),
          type: "number",
          step: 0.1,
          min: 0,
          max: 10000,
        },
        expressionProperties: {
          "templateOptions.disabled": `!model.subscription.${flag}`,
        },
      },
    ];
  }

  /**
   * Explain why a change of a linked service flag was refused (and reverted)
   *
   * @since 3.0.0
   *
   * @param refusal Reason of the refusal
   */
  onServiceLinkRefused(refusal: ServiceLinkRefusal): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("companies.dialog.serviceLinks.title"),
        message: this.translateService.instant(`companies.dialog.serviceLinks.${refusal}`),
        btnCancelText: this.translateService.instant("common.cta.close"),
        btnCancelColor: "primary",
        messageOnly: true,
      },
      width: "500px",
    });
  }

  /**
   * Autofill subscription values based on subscription type changes
   *
   * @since 1.0.0
   */
  updateSubscriptionCodes(): void {
    const subscriptionControl = this.formSC.controls.subscription;

    // Control fields
    const keyControl = this.formSC.controls.subscription.get("key");
    const startDateControl = this.formSC.controls.subscription.get("startDate");
    const suppliersControl = this.formSC.controls.subscription.get(
      "suppliers"
    );
    const startDateNfcAccessControl = this.formSC.controls.subscription.get(
      "startDateNfcAccess"
    );
    const startDateSuppliersControl = this.formSC.controls.subscription.get("startDateSuppliers");
    const startDatePrivacyControl =
      this.formSC.controls.subscription.get("startDatePrivacy");

    // Value fields
    const feeField = subscriptionControl.get("fee");
    const startDateField = subscriptionControl.get("startDate");
    const endDateField = subscriptionControl.get("endDate");
    const availableSpaceField = subscriptionControl.get("availableSpace");
    const structureField = subscriptionControl.get("structure");
    const structurePriceField = subscriptionControl.get("structurePrice");
    const firstPriceListField = subscriptionControl.get("firstPriceList");
    const scanInsNextField = subscriptionControl.get("scanInsNext");
    const flatRateField = subscriptionControl.get("flatRate");
    const scanNextField = subscriptionControl.get("scanNext");
    const flatRateScanField = subscriptionControl.get("flatRateScan");
    const startDateSuppliersField = subscriptionControl.get("startDateSuppliers");
    const endDateSuppliersField = subscriptionControl.get("endDateSuppliers");
    const startDateNfcAccessField = subscriptionControl.get(
      "startDateNfcAccess"
    );
    const endDateNfcAccessField = subscriptionControl.get("endDateNfcAccess");
    const extraSpaceFeeField = subscriptionControl.get("extraSpaceFee");

    const privacyField = subscriptionControl.get("privacy");
    const privacySagField = subscriptionControl.get("privacySag");

    const privacyControl = this.formSC.controls.subscription.get(
      "privacy"
    );
    const privacySagControl = this.formSC.controls.subscription.get(
      "privacySag"
    );
    const startDatePrivacyField = subscriptionControl.get("startDatePrivacy");
    const endDatePrivacyField = subscriptionControl.get("endDatePrivacy");

    // Option fields
    let codeField: FormlyFieldConfig;
    if (this.contractFields && Array.isArray(this.contractFields)) {
      this.contractFields.forEach((el: any) => {
        if (el.fieldGroup && Array.isArray(el.fieldGroup)) {
          el.fieldGroup.forEach((subEl: any) => {
            switch (subEl.key) {
              case "subscription.code":
                codeField = subEl;
                break;
            }
          });
        }
      });
    }

    if (keyControl) {
      keyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(keyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }

            let index = -1;
            switch (res) {
              case "noSubscription":
              case "PRIVACY":
                feeField.setValue(0);
                availableSpaceField.setValue(0);
                extraSpaceFeeField.setValue(0);
                structurePriceField.setValue(0);
                structureField.setValue(false);
                firstPriceListField.setValue(false);
                scanInsNextField.setValue(false);
                flatRateField.setValue(false);
                scanNextField.setValue(false);
                flatRateScanField.setValue(false);
                break;
              case "FULL":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULL";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FULLBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FULLBASIC";
                });
                structureField.setValue(true);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              
              case "FREE":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREE";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(false);
                flatRateField.setValue(true);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
              case "FREEBASIC":
                index = this.archediSubscriptions.findIndex((el: any) => {
                  return el.key === "FREEBASIC";
                });
                structureField.setValue(false);
                firstPriceListField.setValue(true);
                scanInsNextField.setValue(true);
                flatRateField.setValue(false);
                scanNextField.setValue(true);
                flatRateScanField.setValue(false);
                break;
            }
            if (index > -1) {
              feeField.setValue(this.archediSubscriptions[index].fee);
              availableSpaceField.setValue(
                this.archediSubscriptions[index].availableSpace
              );
              extraSpaceFeeField.setValue(
                this.archediSubscriptions[index].extraSpaceFee
              );
              structurePriceField.setValue(
                this.archediSubscriptions[index].structurePrice
              );
            }

            // Backend requires a billing code whenever the matching flag is enabled.
            // Only one code is currently defined per list, so it is used directly
            // rather than filtered by plan (their "keys" arrays are inconsistently
            // maintained and do not cover every plan that sets these flags, e.g. FULLBASIC).
            const scanInsNextCode = this.configService.availableInsCodesNext[0];
            const flatRateCode = this.configService.availableInsCodesNextForfait[0];
            const scanNextCode = this.configService.availableScanCodesNext[0];
            const flatRateScanCode = this.configService.availableScanCodesNextForfait[0];
            this.modelSC.subscription = this.modelSC.subscription || {};
            this.modelSC.subscription.scanInsNextCode =
              scanInsNextField && scanInsNextField.value && scanInsNextCode
                ? scanInsNextCode.value
                : "INSSUCC001";
            this.modelSC.subscription.flatRateCode =
              flatRateField && flatRateField.value && flatRateCode
                ? flatRateCode.value
                : "FINSSUCC001";
            this.modelSC.subscription.scanNextCode =
              scanNextField && scanNextField.value && scanNextCode
                ? scanNextCode.value
                : "SCASUCC001";
            this.modelSC.subscription.flatRateScanCode =
              flatRateScanField && flatRateScanField.value && flatRateScanCode
                ? flatRateScanCode.value
                : "FSCASUCC001";
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    // Links between services ("Nota informativa", "Valutazione fornitori"): turning a
    // service on selects the ones it requires, which cannot be turned off while it is on
    this.subscriptions.push(
      ...bindServiceLinks(
        subscriptionControl,
        (refusal) => this.onServiceLinkRefused(refusal),
        (err) => this.helperService.handleError(err)
      )
    );

    if (startDateControl) {
      startDateControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDateControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setFullYear(start.getFullYear() + 1);
              endDateField.setValue(end);

              // if (nfcAccessControl && !nfcAccessControl.value) {
              //   if (startDateNfcAccessField) {
              //     const date = startDateField.value;
              //     startDateNfcAccessField.setValue(date);
              //   }
              //   if (endDateNfcAccessField) {
              //     const date = endDateField.value;
              //     endDateNfcAccessField.setValue(date);
              //   }
              // }
              if (suppliersControl && !suppliersControl.value) {
                if (startDateSuppliersField) {
                  const date = startDateField.value;
                  startDateSuppliersField.setValue(date);
                }
                if (endDateSuppliersField) {
                  const date = endDateField.value;
                  endDateSuppliersField.setValue(date);
                }
              }
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }


    if (startDateNfcAccessControl) {
      startDateNfcAccessControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDateNfcAccessControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDateNfcAccessField.dirty) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(start.getFullYear() + 1);
              endDateNfcAccessField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDateSuppliersControl) {
      startDateSuppliersControl.valueChanges
      .pipe(distinctUntilChanged(), startWith(startDateSuppliersControl.value))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          }
          if (res && startDateSuppliersField.dirty) {
            // Set endDate automatically to startDate + 1 year
            const start = new Date(res);
            const end = start;
            end.setDate(start.getDate() - 1);
            end.setFullYear(end.getFullYear() + 1);
            endDateSuppliersField.setValue(end);
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
    }

    if (privacyControl) {
      privacyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(privacyControl.value))
        .subscribe(
          (res) => {
            // Skipped when the deselection is going to be refused (and reverted) by the
            // service links: the privacy dates must survive it
            if (!res && !activeServicesRequiring(subscriptionControl.value, "privacy").length) {
              if (privacySagControl !== null && !privacySagControl.value && startDatePrivacyField !== null && endDatePrivacyField !== null) {
                startDatePrivacyField.setValue(null);
                endDatePrivacyField.setValue(null);
              }
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }
    if (privacySagControl) {
      privacySagControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(privacySagControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              if (privacyControl !== null && !privacyControl.value && startDatePrivacyField !== null && endDatePrivacyField !== null) {
                startDatePrivacyField.setValue(null);
                endDatePrivacyField.setValue(null);
              }
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }

    if (startDatePrivacyControl) {
      startDatePrivacyControl.valueChanges
        .pipe(distinctUntilChanged(), startWith(startDatePrivacyControl.value))
        .subscribe(
          (res) => {
            if (!res) {
              return;
            }
            if (res && startDatePrivacyField !== null && startDatePrivacyField.dirty && endDatePrivacyField !== null) {
              // Set endDate automatically to startDate + 1 year
              const start = new Date(res);
              const end = start;
              end.setDate(start.getDate() - 1);
              end.setFullYear(end.getFullYear() + 1);
              endDatePrivacyField.setValue(end);
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(formNumber: number): void {
    if (formNumber === 1) {
      // Prevent invalid form submit
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid"),
          this.translateService.instant("common.words.error")
        );
        console.warn(this.form);
        return;
      }

      // Activate submit button loading status
      this.formBtnOptions.active = true;
      this.formBtnOptionsSC.active = true;

      // Remove subscription from model if company is not of "manager" type
      if (this.model && this.model.types && !this.model.types.includes("manager")) {
        if (this.model.subscription) {
          delete this.model.subscription;
        }
      } else {
        this.model.subscription = this.modelSCInit.subscription;
      }
    

      this.subscriptions.push(
        this.companiesService
          .create(this.model)
          .pipe(finalize(() => {
            this.formBtnOptions.active = false;
            this.formBtnOptionsSC.active = false;
          }))
          .subscribe(
            (res) => {
              if (res.status) {
                this.toastrService.success(res.message);
                setTimeout(() => {
                  const lang = this.translateService.currentLang;
                  this.router.navigate([
                    lang,
                    "app",
                    "companies",
                    "detail",
                    res.data._id,
                  ]);
                }, this.configService.redirectDelayAfterCreation);
                return;
              }
            },
            (err) => {
              // Choose one of the following error handling method.
              // 1) First one show a message right under the form fields
              //    (if the form is properly setted)
              // 2) Second one is used to show a mat-error under each field
              //    in the dynamic form (is meant to be used only in dynamicForm)
              // 3) Third one show toastr notifications for each error
              // 4) Fourth one is used to show mat-error under each field
              //    in the ngx-formly form (is mean to be used only with ngx-formly)
              // this.helperService.handleFormError(form, err);
              // this.helperService.handleDynamicFormError(form, err);
              // this.helperService.handleError(err);
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }

    if (formNumber === 2) {
      // Prevent invalid form submit
      if (this.form.invalid || this.formSC.invalid) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsTouched();
        });
        Object.keys(this.formSC.controls).forEach((key) => {
          this.formSC.controls[key].markAsTouched();
        });
        this.toastrService.error(
          this.translateService.instant("common.errors.form_is_invalid"),
          this.translateService.instant("common.words.error")
        );
        return;
      }

      // Activate submit button loading status
      this.formBtnOptions.active = true;
      this.formBtnOptionsSC.active = true;

      // Remove subscription from model if company is not of "manager" type
      if (this.model && this.model.types && !this.model.types.includes("manager")) {
        if (this.model.subscription) {
          delete this.model.subscription;
        }
      } else {
        this.model.subscription = this.modelSC.subscription;
      }
    

      this.subscriptions.push(
        this.companiesService
          .create(this.model)
          .pipe(finalize(() => {
            this.formBtnOptions.active = false;
            this.formBtnOptionsSC.active = false;
          }))
          .subscribe(
            (res) => {
              if (res.status) {
                this.toastrService.success(res.message);
                setTimeout(() => {
                  const lang = this.translateService.currentLang;
                  this.router.navigate([
                    lang,
                    "app",
                    "companies",
                    "detail",
                    res.data._id,
                  ]);
                }, this.configService.redirectDelayAfterCreation);
                return;
              }
            },
            (err) => {
              // Choose one of the following error handling method.
              // 1) First one show a message right under the form fields
              //    (if the form is properly setted)
              // 2) Second one is used to show a mat-error under each field
              //    in the dynamic form (is meant to be used only in dynamicForm)
              // 3) Third one show toastr notifications for each error
              // 4) Fourth one is used to show mat-error under each field
              //    in the ngx-formly form (is mean to be used only with ngx-formly)
              // this.helperService.handleFormError(form, err);
              // this.helperService.handleDynamicFormError(form, err);
              // this.helperService.handleError(err);
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }

  }
}
